// src/pages/admin/PostEditor.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import ReactQuill, { Quill } from "react-quill";
import ImageResize from "quill-image-resize-module-react";
import "react-quill/dist/quill.snow.css";

import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { uploadBlogImage } from "../../lib/adminApi";
import { API_URL } from "../../lib/utils";

Quill.register("modules/imageResize", ImageResize);

const categories = [
  { value: "humanitarian_impact", label: "Humanitarian Impact" },
  { value: "child_welfare", label: "Child Welfare & Education" },
  { value: "rights_inclusion", label: "Rights & Inclusion" },
  { value: "technology_for_good", label: "Technology for Good" },
  { value: "climate_community", label: "Climate & Community" },
  { value: "partner_stories", label: "Partner/Volunteer Stories" },
];

const EMPTY_FORM = {
  title: "",
  excerpt: "",
  content: "<p></p>",
  categories: ["humanitarian_impact"],
  category: "humanitarian_impact",
  author: "",
  image_url: "",
  tags: [],
  is_published: true,
};

const FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1559027615-cd4628902d4a?w=1200&q=80";

const BACKEND_ORIGIN = API_URL.replace(/\/api\/?$/, "");

const normalizeImageUrl = (url) => {
  if (!url || typeof url !== "string") return "";
  const clean = url.trim();
  if (!clean) return "";
  if (/^https?:\/\//i.test(clean)) return clean;
  if (clean.startsWith("/")) return `${BACKEND_ORIGIN}${clean}`;
  return `${BACKEND_ORIGIN}/${clean}`;
};

const uid = () =>
  `block_${Date.now()}_${Math.random().toString(16).slice(2)}`;

const escapeHtml = (value = "") =>
  String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");

const getYouTubeEmbed = (url) => {
  const clean = String(url || "").trim();
  const patterns = [
    /youtube\.com\/watch\?v=([^&]+)/i,
    /youtu\.be\/([^?]+)/i,
    /youtube\.com\/shorts\/([^?]+)/i,
    /youtube\.com\/embed\/([^?]+)/i,
  ];

  for (const pattern of patterns) {
    const match = clean.match(pattern);
    if (match?.[1]) return `https://www.youtube.com/embed/${match[1]}`;
  }

  return "";
};

const buildEmbedHtml = (url, blockId = uid()) => {
  const clean = String(url || "").trim();
  if (!clean) return "";

  const youtube = getYouTubeEmbed(clean);

  if (youtube) {
    return `
      <div data-post-block="embed" data-embed-url="${escapeHtml(clean)}" data-block-id="${blockId}" class="my-8 overflow-hidden rounded-2xl border border-stone-200 bg-black shadow-sm">
        <div class="relative w-full pt-[56.25%]">
          <iframe src="${youtube}" class="absolute left-0 top-0 h-full w-full" frameborder="0" allowfullscreen loading="lazy"></iframe>
        </div>
      </div>
    `;
  }

  if (clean.includes("facebook.com")) {
    return `
      <div data-post-block="embed" data-embed-url="${escapeHtml(clean)}" data-block-id="${blockId}" class="my-8 overflow-hidden rounded-2xl border border-stone-200 bg-stone-50 p-4">
        <iframe src="https://www.facebook.com/plugins/post.php?href=${encodeURIComponent(
          clean
        )}&show_text=true&width=700" class="mx-auto w-full min-h-[420px]" frameborder="0" scrolling="no" allowfullscreen loading="lazy"></iframe>
      </div>
    `;
  }

  if (clean.includes("instagram.com") || clean.includes("tiktok.com")) {
    return `
      <div data-post-block="embed" data-embed-url="${escapeHtml(clean)}" data-block-id="${blockId}" class="my-8 rounded-2xl border border-stone-200 bg-stone-50 p-5">
        <p class="mb-3 text-sm font-semibold text-stone-700">Embedded social post</p>
        <a href="${escapeHtml(clean)}" target="_blank" rel="noopener noreferrer" class="break-words text-amber-700 underline">${escapeHtml(clean)}</a>
      </div>
    `;
  }

  return `
    <div data-post-block="embed" data-embed-url="${escapeHtml(clean)}" data-block-id="${blockId}" class="my-8 rounded-2xl border border-stone-200 bg-stone-50 p-5">
      <a href="${escapeHtml(clean)}" target="_blank" rel="noopener noreferrer" class="break-words text-amber-700 underline">${escapeHtml(clean)}</a>
    </div>
  `;
};

const widthToClass = (width) => {
  if (width === "33") return "md:w-1/3";
  if (width === "50") return "md:w-1/2";
  if (width === "75") return "md:w-3/4";
  return "w-full";
};

const galleryColsToClass = (cols) => {
  if (Number(cols) === 6) return "md:grid-cols-6";
  if (Number(cols) === 4) return "md:grid-cols-4";
  return "md:grid-cols-2";
};

const removeWidthClasses = (el) => {
  if (!el) return;
  ["w-full", "md:w-1/3", "md:w-1/2", "md:w-3/4"].forEach((c) =>
    el.classList.remove(c)
  );
};

const removeGalleryClasses = (el) => {
  if (!el) return;
  ["md:grid-cols-2", "md:grid-cols-4", "md:grid-cols-6"].forEach((c) =>
    el.classList.remove(c)
  );
};

export default function PostEditor({
  form,
  setForm,
  onSubmit,
  loading = false,
  submitText = "Save Post",
}) {
  const quillRef = useRef(null);
  const singleImageInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const selectedElementRef = useRef(null);

  const [uploadingImage, setUploadingImage] = useState(false);
  const [showEmbedBox, setShowEmbedBox] = useState(false);
  const [embedUrl, setEmbedUrl] = useState("");

  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryCols, setGalleryCols] = useState(2);
  const [galleryImages, setGalleryImages] = useState([]);

  const [contextMenu, setContextMenu] = useState(null);
  const [selectedBlock, setSelectedBlock] = useState(null);

  const safeForm = useMemo(() => {
    const merged = { ...EMPTY_FORM, ...(form || {}) };

    if (!Array.isArray(merged.categories) || merged.categories.length === 0) {
      merged.categories = merged.category ? [merged.category] : [...EMPTY_FORM.categories];
    }

    if (!Array.isArray(merged.tags)) merged.tags = [];
    if (!merged.content || typeof merged.content !== "string") merged.content = "<p></p>";

    const primary =
      (merged.category && String(merged.category).trim()) ||
      merged.categories[0] ||
      EMPTY_FORM.category;

    merged.category = primary;
    merged.categories = [primary, ...merged.categories.filter((c) => c !== primary)];

    return merged;
  }, [form]);

  const safeSetForm = (updater) => {
    setForm((prev) => {
      const base = { ...EMPTY_FORM, ...(prev || {}) };
      const next = typeof updater === "function" ? updater(base) : updater;

      const cats =
        Array.isArray(next.categories) && next.categories.length
          ? next.categories
          : [next.category || EMPTY_FORM.category];

      const primary = next.category || cats[0] || EMPTY_FORM.category;

      return {
        ...next,
        category: primary,
        categories: [primary, ...cats.filter((c) => c !== primary)],
        tags: Array.isArray(next.tags) ? next.tags : [],
        content: next.content || "<p></p>",
      };
    });
  };

  const getEditor = () => quillRef.current?.getEditor();

  const syncEditorContent = () => {
    const html = getEditor()?.root?.innerHTML || "<p></p>";
    safeSetForm((s) => ({ ...s, content: html }));
  };

  const clearSelectionOutline = () => {
    const root = getEditor()?.root;
    root?.querySelectorAll("[data-selected-block='true']").forEach((el) => {
      el.removeAttribute("data-selected-block");
      el.classList.remove("ring-4", "ring-amber-500", "ring-offset-2");
    });
  };

  const readSelectedElement = (el) => {
    if (!el) return null;

    if (el.tagName === "A") {
      return {
        type: "link",
        href: el.getAttribute("href") || "",
        text: el.textContent || "",
        target: el.getAttribute("target") || "",
      };
    }

    const type = el.getAttribute("data-post-block");

    if (type === "image") {
      const img = el.querySelector("img");
      const caption = el.querySelector("figcaption");
      return {
        type: "image",
        src: img?.getAttribute("src") || "",
        alt: img?.getAttribute("alt") || "",
        caption: caption?.textContent || "",
        width: el.getAttribute("data-width") || "full",
      };
    }

    if (type === "gallery") {
      const figures = Array.from(el.querySelectorAll("figure"));
      return {
        type: "gallery",
        cols: Number(el.getAttribute("data-cols") || 2),
        items: figures.map((fig, index) => ({
          index,
          src: fig.querySelector("img")?.getAttribute("src") || "",
          caption: fig.querySelector("figcaption")?.textContent || "",
        })),
      };
    }

    if (type === "embed") {
      return {
        type: "embed",
        url: el.getAttribute("data-embed-url") || "",
      };
    }

    return null;
  };

  const selectEditorBlock = (element) => {
    if (!element) return;

    clearSelectionOutline();
    const block =
      element.closest("[data-post-block]") ||
      (element.tagName === "A" ? element : element.closest("a"));

    if (!block) {
      selectedElementRef.current = null;
      setSelectedBlock(null);
      return;
    }

    selectedElementRef.current = block;
    block.setAttribute("data-selected-block", "true");
    block.classList.add("ring-4", "ring-amber-500", "ring-offset-2");

    setSelectedBlock(readSelectedElement(block));
  };

  const refreshSelectedBlock = () => {
    setSelectedBlock(readSelectedElement(selectedElementRef.current));
  };

  const deleteSelectedBlock = () => {
    const el = selectedElementRef.current;
    if (!el) return;

    if (el.tagName === "A") {
      el.replaceWith(document.createTextNode(el.textContent || ""));
    } else {
      el.remove();
    }

    selectedElementRef.current = null;
    setSelectedBlock(null);
    setContextMenu(null);
    syncEditorContent();
  };

  const insertHtml = (html) => {
    const editor = getEditor();
    if (!editor) return;

    const range = editor.getSelection(true);
    const index = range?.index ?? editor.getLength();
    editor.clipboard.dangerouslyPasteHTML(index, html);
    editor.setSelection(index + 1);
    setTimeout(syncEditorContent, 0);
  };

  const uploadFile = async (file) => {
    const data = await uploadBlogImage(file);
    if (!data?.image_url) throw new Error("Upload failed");
    return normalizeImageUrl(data.image_url);
  };

  const insertSingleImage = async (file) => {
    if (!file) return;

    setUploadingImage(true);
    try {
      const url = await uploadFile(file);
      const blockId = uid();

      insertHtml(`
        <figure data-post-block="image" data-block-id="${blockId}" data-width="full" class="my-8 w-full">
          <img src="${url}" alt="" class="w-full rounded-2xl border border-stone-200 object-cover shadow-sm" loading="lazy" />
          <figcaption class="mt-2 text-center text-sm italic text-stone-500"></figcaption>
        </figure>
      `);
    } catch (err) {
      alert(err?.message || "Image upload failed");
    } finally {
      setUploadingImage(false);
    }
  };

  const openGalleryFiles = async (files) => {
    const selected = Array.from(files || []);
    if (!selected.length) return;

    setUploadingImage(true);
    try {
      const uploaded = [];
      for (const file of selected) {
        const url = await uploadFile(file);
        uploaded.push({ url, caption: "" });
      }
      setGalleryImages(uploaded);
      setGalleryOpen(true);
    } catch (err) {
      alert(err?.message || "Gallery upload failed");
    } finally {
      setUploadingImage(false);
    }
  };

  const insertGallery = () => {
    if (!galleryImages.length) return;

    const blockId = uid();
    const colClass = galleryColsToClass(galleryCols);

    const html = `
      <div data-post-block="gallery" data-blog-gallery="true" data-block-id="${blockId}" data-cols="${galleryCols}" class="my-10 grid grid-cols-1 ${colClass} gap-4">
        ${galleryImages
          .map(
            (img, index) => `
            <figure data-gallery-item="${index}" class="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
              <img src="${img.url}" alt="${escapeHtml(img.caption || "")}" class="h-56 w-full object-cover" loading="lazy" />
              <figcaption class="px-3 py-2 text-center text-sm italic text-stone-500">${escapeHtml(
                img.caption || ""
              )}</figcaption>
            </figure>
          `
          )
          .join("")}
      </div>
    `;

    insertHtml(html);
    setGalleryOpen(false);
    setGalleryImages([]);
  };

  const insertEmbed = () => {
    const html = buildEmbedHtml(embedUrl);
    if (!html) return;
    insertHtml(html);
    setEmbedUrl("");
    setShowEmbedBox(false);
  };

  const onFeaturedImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const localPreview = URL.createObjectURL(file);
    safeSetForm((prev) => ({ ...prev, image_url: localPreview }));

    try {
      setUploadingImage(true);
      const data = await uploadBlogImage(file);
      if (data?.image_url) {
        safeSetForm((prev) => ({ ...prev, image_url: data.image_url }));
      }
    } catch (err) {
      alert(err?.message || "Image upload failed");
    } finally {
      setUploadingImage(false);
      e.target.value = "";
    }
  };

  const toggleCategory = (value) => {
    safeSetForm((prev) => {
      const current = Array.isArray(prev.categories) ? [...prev.categories] : [];
      let next = current.includes(value)
        ? current.filter((c) => c !== value)
        : [...current, value];

      if (!next.length) next = [EMPTY_FORM.category];

      return {
        ...prev,
        categories: next,
        category: next[0],
      };
    });
  };

  const updateImageSetting = (key, value) => {
    const figure = selectedElementRef.current;
    if (!figure || selectedBlock?.type !== "image") return;

    const img = figure.querySelector("img");
    const caption = figure.querySelector("figcaption");

    if (key === "caption" && caption) caption.textContent = value;
    if (key === "alt" && img) img.setAttribute("alt", value);
    if (key === "width") {
      figure.setAttribute("data-width", value);
      removeWidthClasses(figure);
      figure.classList.add(...widthToClass(value).split(" "));
    }

    syncEditorContent();
    refreshSelectedBlock();
  };

  const updateGalleryLayout = (cols) => {
    const gallery = selectedElementRef.current;
    if (!gallery || selectedBlock?.type !== "gallery") return;

    gallery.setAttribute("data-cols", String(cols));
    removeGalleryClasses(gallery);
    gallery.classList.add(...galleryColsToClass(cols).split(" "));

    syncEditorContent();
    refreshSelectedBlock();
  };

  const updateGalleryCaption = (index, caption) => {
    const gallery = selectedElementRef.current;
    if (!gallery || selectedBlock?.type !== "gallery") return;

    const fig = gallery.querySelector(`[data-gallery-item="${index}"]`);
    const cap = fig?.querySelector("figcaption");
    const img = fig?.querySelector("img");

    if (cap) cap.textContent = caption;
    if (img) img.setAttribute("alt", caption);

    syncEditorContent();
    refreshSelectedBlock();
  };

  const deleteGalleryItem = (index) => {
    const gallery = selectedElementRef.current;
    if (!gallery || selectedBlock?.type !== "gallery") return;

    gallery.querySelector(`[data-gallery-item="${index}"]`)?.remove();

    const remaining = Array.from(gallery.querySelectorAll("figure"));
    remaining.forEach((fig, i) => fig.setAttribute("data-gallery-item", String(i)));

    if (!remaining.length) {
      gallery.remove();
      selectedElementRef.current = null;
      setSelectedBlock(null);
    }

    syncEditorContent();
    refreshSelectedBlock();
  };

  const updateLinkSetting = (key, value) => {
    const link = selectedElementRef.current;
    if (!link || selectedBlock?.type !== "link") return;

    if (key === "href") link.setAttribute("href", value);
    if (key === "text") link.textContent = value;
    if (key === "target") {
      if (value) {
        link.setAttribute("target", "_blank");
        link.setAttribute("rel", "noopener noreferrer");
      } else {
        link.removeAttribute("target");
        link.removeAttribute("rel");
      }
    }

    syncEditorContent();
    refreshSelectedBlock();
  };

  const updateEmbedUrl = (url) => {
    const embed = selectedElementRef.current;
    if (!embed || selectedBlock?.type !== "embed") return;

    const blockId = embed.getAttribute("data-block-id") || uid();
    const wrapper = document.createElement("div");
    wrapper.innerHTML = buildEmbedHtml(url, blockId).trim();
    const newEmbed = wrapper.firstElementChild;

    if (!newEmbed) return;

    embed.replaceWith(newEmbed);
    selectedElementRef.current = newEmbed;
    syncEditorContent();
    refreshSelectedBlock();
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if (!selectedElementRef.current) return;

      const active = document.activeElement;
      const isTypingField =
        active?.tagName === "INPUT" ||
        active?.tagName === "TEXTAREA" ||
        active?.isContentEditable;

      if (isTypingField) return;

      if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        deleteSelectedBlock();
      }

      if (e.key === "Escape") {
        clearSelectionOutline();
        selectedElementRef.current = null;
        setSelectedBlock(null);
        setContextMenu(null);
      }
    };

    const closeMenu = () => setContextMenu(null);

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("click", closeMenu);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("click", closeMenu);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBlock]);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, 4, false] }],
          [{ font: [] }],
          [{ size: ["small", false, "large", "huge"] }],
          ["bold", "italic", "underline", "strike"],
          [{ color: [] }, { background: [] }],
          [{ align: [] }],
          [{ list: "ordered" }, { list: "bullet" }],
          [{ indent: "-1" }, { indent: "+1" }],
          ["blockquote", "code-block"],
          ["link", "image", "video"],
          ["clean"],
        ],
        handlers: {
          image: () => singleImageInputRef.current?.click(),
        },
      },
      imageResize: {
        parchment: Quill.import("parchment"),
        modules: ["Resize", "DisplaySize", "Toolbar"],
      },
      clipboard: {
        matchVisual: false,
      },
    }),
    []
  );

  const formats = [
    "header",
    "font",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "color",
    "background",
    "align",
    "list",
    "bullet",
    "indent",
    "blockquote",
    "code-block",
    "link",
    "image",
    "video",
    "width",
    "height",
    "style",
  ];

  const previewSrc = normalizeImageUrl(safeForm.image_url);
  const tagsString = safeForm.tags.join(", ");

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <Card className="rounded-2xl border-stone-200">
        <CardContent className="space-y-5 p-6">
          <div>
            <Label htmlFor="post-title">Title</Label>
            <Input
              id="post-title"
              value={safeForm.title}
              onChange={(e) => safeSetForm((s) => ({ ...s, title: e.target.value }))}
              required
            />
          </div>

          <div>
            <Label htmlFor="post-excerpt">Excerpt</Label>
            <Textarea
              id="post-excerpt"
              value={safeForm.excerpt}
              onChange={(e) => safeSetForm((s) => ({ ...s, excerpt: e.target.value }))}
              rows={3}
              required
            />
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div className="md:col-span-2">
              <div className="mb-2 flex items-center justify-between gap-3">
                <Label>Categories</Label>
                <span className="text-xs text-stone-500">
                  Primary: <b>{categories.find((c) => c.value === safeForm.category)?.label}</b>
                </span>
              </div>

              <div className="flex flex-wrap gap-2">
                {categories.map((c) => {
                  const checked = safeForm.categories.includes(c.value);

                  return (
                    <button
                      key={c.value}
                      type="button"
                      onClick={() => toggleCategory(c.value)}
                      className={`rounded-xl border px-3 py-2 text-sm transition ${
                        checked
                          ? "border-amber-300 bg-amber-100 text-amber-900"
                          : "border-stone-200 bg-white text-stone-700 hover:bg-stone-50"
                      }`}
                    >
                      {c.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label htmlFor="post-author">Author</Label>
              <Input
                id="post-author"
                value={safeForm.author}
                onChange={(e) => safeSetForm((s) => ({ ...s, author: e.target.value }))}
                required
              />
            </div>
          </div>

          <div>
            <Label htmlFor="post-tags">Tags</Label>
            <Input
              id="post-tags"
              value={tagsString}
              onChange={(e) =>
                safeSetForm((s) => ({
                  ...s,
                  tags: e.target.value
                    .split(",")
                    .map((t) => t.trim())
                    .filter(Boolean),
                }))
              }
              placeholder="impact, education, outreach"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="post-image">Featured Image</Label>
            <Input id="post-image" type="file" accept="image/*" onChange={onFeaturedImageChange} />

            {previewSrc ? (
              <img
                src={previewSrc}
                alt="Preview"
                className="max-h-72 w-full rounded-xl border object-cover"
                onError={(e) => {
                  e.currentTarget.src = FALLBACK_IMAGE;
                }}
              />
            ) : null}
          </div>

          <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_340px]">
            <div className="rounded-2xl border border-stone-200 bg-stone-50 p-4">
              <div className="mb-3 flex flex-wrap gap-2">
                <Button
                  type="button"
                  onClick={() => singleImageInputRef.current?.click()}
                  className="rounded-xl bg-stone-900 hover:bg-stone-800"
                >
                  Add Single Image
                </Button>

                <Button
                  type="button"
                  onClick={() => galleryInputRef.current?.click()}
                  className="rounded-xl bg-amber-700 hover:bg-amber-800"
                >
                  Add Gallery
                </Button>

                <Button
                  type="button"
                  onClick={() => setShowEmbedBox(true)}
                  className="rounded-xl bg-blue-700 hover:bg-blue-800"
                >
                  Embed Video/Social Link
                </Button>
              </div>

              <input
                ref={singleImageInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  insertSingleImage(e.target.files?.[0]);
                  e.target.value = "";
                }}
              />

              <input
                ref={galleryInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => {
                  openGalleryFiles(e.target.files);
                  e.target.value = "";
                }}
              />

              {showEmbedBox ? (
                <div className="mb-4 rounded-xl border border-stone-200 bg-white p-4">
                  <Label>Paste YouTube, Facebook, Instagram, TikTok or any link</Label>
                  <div className="mt-2 flex gap-2">
                    <Input
                      value={embedUrl}
                      onChange={(e) => setEmbedUrl(e.target.value)}
                      placeholder="https://youtube.com/watch?v=..."
                    />
                    <Button type="button" onClick={insertEmbed} className="bg-amber-700 hover:bg-amber-800">
                      Insert
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setShowEmbedBox(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : null}

              <div
                className="blog-editor-wrapper rounded-xl bg-white"
                onClick={(e) => {
                  e.stopPropagation();
                  selectEditorBlock(e.target);
                }}
                onContextMenu={(e) => {
                  const target = e.target;
                  const block =
                    target?.closest?.("[data-post-block]") ||
                    (target?.tagName === "A" ? target : target?.closest?.("a"));

                  if (block) {
                    e.preventDefault();
                    e.stopPropagation();
                    selectEditorBlock(block);
                    setContextMenu({
                      x: e.clientX,
                      y: e.clientY,
                    });
                  }
                }}
              >
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={safeForm.content}
                  onChange={(value) => safeSetForm((s) => ({ ...s, content: value }))}
                  modules={modules}
                  formats={formats}
                  className="min-h-[520px]"
                />
              </div>

              {contextMenu ? (
                <div
                  onClick={(e) => e.stopPropagation()}
                  className="fixed z-[9999] w-56 rounded-xl border border-stone-200 bg-white p-2 shadow-xl"
                  style={{ left: contextMenu.x, top: contextMenu.y }}
                >
                  <button
                    type="button"
                    className="block w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-stone-100"
                    onClick={() => {
                      setContextMenu(null);
                      refreshSelectedBlock();
                    }}
                  >
                    Open Block Settings
                  </button>

                  <button
                    type="button"
                    className="block w-full rounded-lg px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50"
                    onClick={deleteSelectedBlock}
                  >
                    Delete Selected Block
                  </button>
                </div>
              ) : null}
            </div>

            <aside className="rounded-2xl border border-stone-200 bg-white p-4 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-base font-bold text-stone-900">Block Settings</h3>
                {selectedBlock ? (
                  <button
                    type="button"
                    onClick={() => {
                      clearSelectionOutline();
                      selectedElementRef.current = null;
                      setSelectedBlock(null);
                    }}
                    className="rounded-lg bg-stone-100 px-2 py-1 text-xs text-stone-700 hover:bg-stone-200"
                  >
                    Clear
                  </button>
                ) : null}
              </div>

              {!selectedBlock ? (
                <div className="rounded-xl bg-stone-50 p-4 text-sm text-stone-600">
                  Click an image, gallery, embed, or link inside the post body to manage it here.
                </div>
              ) : null}

              {selectedBlock?.type === "image" ? (
                <div className="space-y-4">
                  <div>
                    <Label>Image URL</Label>
                    <Input value={selectedBlock.src} readOnly className="text-xs" />
                  </div>

                  <div>
                    <Label>Caption</Label>
                    <Textarea
                      value={selectedBlock.caption}
                      rows={3}
                      placeholder="Optional caption"
                      onChange={(e) => updateImageSetting("caption", e.target.value)}
                    />
                  </div>

                  <div>
                    <Label>Alt Text</Label>
                    <Input
                      value={selectedBlock.alt}
                      placeholder="Describe the image"
                      onChange={(e) => updateImageSetting("alt", e.target.value)}
                    />
                  </div>

                  <div>
                    <Label>Image Width</Label>
                    <select
                      value={selectedBlock.width}
                      onChange={(e) => updateImageSetting("width", e.target.value)}
                      className="mt-1 w-full rounded-xl border border-stone-300 px-3 py-2 text-sm"
                    >
                      <option value="full">Full Width</option>
                      <option value="75">75%</option>
                      <option value="50">50%</option>
                      <option value="33">33%</option>
                    </select>
                  </div>

                  <Button type="button" onClick={deleteSelectedBlock} className="w-full bg-red-600 hover:bg-red-700">
                    Delete Image
                  </Button>
                </div>
              ) : null}

              {selectedBlock?.type === "gallery" ? (
                <div className="space-y-4">
                  <div>
                    <Label>Gallery Layout</Label>
                    <select
                      value={selectedBlock.cols}
                      onChange={(e) => updateGalleryLayout(Number(e.target.value))}
                      className="mt-1 w-full rounded-xl border border-stone-300 px-3 py-2 text-sm"
                    >
                      <option value={2}>Tiled 2 Columns</option>
                      <option value={4}>Tiled 4 Columns</option>
                      <option value={6}>Tiled 6 Columns</option>
                    </select>
                  </div>

                  <div className="space-y-3">
                    <Label>Gallery Images</Label>
                    {selectedBlock.items.map((item) => (
                      <div key={`${item.src}-${item.index}`} className="rounded-xl border border-stone-200 p-3">
                        <img src={item.src} alt="" className="mb-2 h-28 w-full rounded-lg object-cover" />
                        <Input
                          value={item.caption}
                          placeholder="Optional caption"
                          onChange={(e) => updateGalleryCaption(item.index, e.target.value)}
                        />
                        <button
                          type="button"
                          onClick={() => deleteGalleryItem(item.index)}
                          className="mt-2 text-sm font-semibold text-red-600 hover:text-red-700"
                        >
                          Delete this image
                        </button>
                      </div>
                    ))}
                  </div>

                  <Button type="button" onClick={deleteSelectedBlock} className="w-full bg-red-600 hover:bg-red-700">
                    Delete Gallery
                  </Button>
                </div>
              ) : null}

              {selectedBlock?.type === "link" ? (
                <div className="space-y-4">
                  <div>
                    <Label>Link Text</Label>
                    <Input
                      value={selectedBlock.text}
                      onChange={(e) => updateLinkSetting("text", e.target.value)}
                    />
                  </div>

                  <div>
                    <Label>Link URL</Label>
                    <Input
                      value={selectedBlock.href}
                      onChange={(e) => updateLinkSetting("href", e.target.value)}
                      placeholder="https://..."
                    />
                  </div>

                  <label className="flex items-center gap-2 text-sm text-stone-700">
                    <input
                      type="checkbox"
                      checked={selectedBlock.target === "_blank"}
                      onChange={(e) => updateLinkSetting("target", e.target.checked)}
                    />
                    Open in new tab
                  </label>

                  <Button type="button" onClick={deleteSelectedBlock} className="w-full bg-red-600 hover:bg-red-700">
                    Remove Link
                  </Button>
                </div>
              ) : null}

              {selectedBlock?.type === "embed" ? (
                <div className="space-y-4">
                  <div>
                    <Label>Embed URL</Label>
                    <Input
                      value={selectedBlock.url}
                      onChange={(e) => setSelectedBlock((s) => ({ ...s, url: e.target.value }))}
                      onBlur={(e) => updateEmbedUrl(e.target.value)}
                      placeholder="https://youtube.com/..."
                    />
                  </div>

                  <Button type="button" onClick={() => updateEmbedUrl(selectedBlock.url)} className="w-full bg-blue-700 hover:bg-blue-800">
                    Update Embed
                  </Button>

                  <Button type="button" onClick={deleteSelectedBlock} className="w-full bg-red-600 hover:bg-red-700">
                    Delete Embed
                  </Button>
                </div>
              ) : null}
            </aside>
          </div>

          <label className="flex items-center gap-2 text-sm text-stone-700">
            <input
              type="checkbox"
              checked={!!safeForm.is_published}
              onChange={(e) => safeSetForm((s) => ({ ...s, is_published: e.target.checked }))}
            />
            Publish immediately
          </label>

          <Button
            type="submit"
            disabled={loading || uploadingImage}
            className="rounded-xl bg-amber-700 hover:bg-amber-800"
          >
            {loading ? "Saving..." : uploadingImage ? "Uploading..." : submitText}
          </Button>
        </CardContent>
      </Card>

      {galleryOpen ? (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/60 p-4">
          <div className="max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-stone-900">Insert Image Gallery</h3>
              <button
                type="button"
                onClick={() => setGalleryOpen(false)}
                className="rounded-full bg-stone-100 px-3 py-1 text-sm"
              >
                Close
              </button>
            </div>

            <div className="mb-5">
              <Label>Gallery Layout</Label>
              <select
                value={galleryCols}
                onChange={(e) => setGalleryCols(Number(e.target.value))}
                className="mt-1 w-full rounded-xl border border-stone-300 px-3 py-2"
              >
                <option value={2}>Tiled 2 Columns</option>
                <option value={4}>Tiled 4 Columns</option>
                <option value={6}>Tiled 6 Columns</option>
              </select>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              {galleryImages.map((img, index) => (
                <div key={img.url} className="rounded-2xl border border-stone-200 p-3">
                  <img src={img.url} alt="" className="mb-3 h-48 w-full rounded-xl object-cover" />
                  <Input
                    value={img.caption}
                    placeholder="Optional caption"
                    onChange={(e) => {
                      const next = [...galleryImages];
                      next[index].caption = e.target.value;
                      setGalleryImages(next);
                    }}
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setGalleryImages((items) => items.filter((_, i) => i !== index))
                    }
                    className="mt-2 text-sm font-semibold text-red-600 hover:text-red-700"
                  >
                    Remove from gallery
                  </button>
                </div>
              ))}
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setGalleryOpen(false)}>
                Cancel
              </Button>
              <Button type="button" onClick={insertGallery} className="bg-amber-700 hover:bg-amber-800">
                Insert Gallery
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      <style>{`
        .blog-editor-wrapper .ql-toolbar {
          border-top-left-radius: 0.75rem;
          border-top-right-radius: 0.75rem;
          border-color: #d6d3d1;
          background: #fafaf9;
        }

        .blog-editor-wrapper .ql-container {
          min-height: 520px;
          border-bottom-left-radius: 0.75rem;
          border-bottom-right-radius: 0.75rem;
          border-color: #d6d3d1;
          font-size: 17px;
          line-height: 1.8;
        }

        .blog-editor-wrapper .ql-editor {
          min-height: 520px;
          padding: 28px;
        }

        .blog-editor-wrapper .ql-editor h1 {
          font-size: 2.25rem;
          font-weight: 800;
          line-height: 1.15;
          margin: 1.2rem 0 0.8rem;
        }

        .blog-editor-wrapper .ql-editor h2 {
          font-size: 1.875rem;
          font-weight: 800;
          line-height: 1.2;
          margin: 1.1rem 0 0.7rem;
        }

        .blog-editor-wrapper .ql-editor h3 {
          font-size: 1.5rem;
          font-weight: 700;
          margin: 1rem 0 0.6rem;
        }

        .blog-editor-wrapper .ql-editor p {
          margin-bottom: 1rem;
        }

        .blog-editor-wrapper .ql-editor img {
          max-width: 100%;
          border-radius: 1rem;
        }

        .blog-editor-wrapper .ql-editor iframe {
          max-width: 100%;
        }

        .blog-editor-wrapper .ql-editor a {
          color: #b45309;
          text-decoration: underline;
        }

        .blog-editor-wrapper [data-post-block],
        .blog-editor-wrapper a {
          cursor: pointer;
        }

        .blog-editor-wrapper [data-post-block="image"] {
          transition: box-shadow 0.2s ease, transform 0.2s ease;
        }

        .blog-editor-wrapper [data-post-block="gallery"] figure {
          transition: opacity 0.2s ease;
        }
      `}</style>
    </form>
  );
}
